/**
 * @file components/audit-tasks/field/FieldPhotoSection.tsx
 * @description 現場打卡拍照區塊（從 QuickFindingForm 拆出獨立化）。
 *
 * - 與 NC 解耦：純粹拍照存證，不夾帶任何 NC 表單欄位
 * - 拍照存入 IndexedDB cache_media（status='local'：離線可用、單機保留，worker 不 flush、eviction 不 evict）
 * - 支援連續拍多張，本地縮圖預覽
 *
 * 注意：cache_media → file upload 的 flusher 為後續任務（不在本 plan 範圍）。
 */

import { useRef, useState } from 'react';
import { Button, Card, Space, Typography } from 'antd';
import { message } from '@/lib/antd-app';
import { Camera } from 'lucide-react';
import { getOfflineDb } from '@/pwa/db';
import { useFieldWriteAccess } from '@/hooks/useFieldWriteAccess';

/** 已暫存照片的本地預覽項 */
interface LocalPhoto {
  /** cache_media 主鍵（UUID） */
  mediaId: string;
  /** objectURL 預覽位址 */
  url: string;
}

interface Props {
  /** 稽核任務 ID（照片 owner，用於日後 flush 對應到任務） */
  taskId: number;
}

/**
 * 現場打卡拍照元件。
 *
 * 點「拍照」喚起相機 → 選取後存入 IndexedDB cache_media（scope='field-photo'，
 * ownerType='task'），不上傳、不夾帶於任何 POST body。
 */
export function FieldPhotoSection({ taskId }: Props) {
  const [photos, setPhotos] = useState<LocalPhoto[]>([]);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  // 序61：管理組（manager）唯讀——無現場寫入能力時隱藏拍照按鈕
  const { canFieldWrite } = useFieldWriteAccess();

  /**
   * 照片選取後存入 IndexedDB cache_media。
   * 不馬上上傳：離線可用、後續由 flusher 任務統一推送。
   */
  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = URL.createObjectURL(file);
      const db = await getOfflineDb();
      const mediaId = crypto.randomUUID();
      await db.put('cache_media', {
        id: mediaId,
        blob: file,
        filename: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        scope: 'field-photo',
        ownerType: 'task',
        ownerId: taskId,
        // 本地 only 照片：worker 不 flush，eviction 不 evict
        status: 'local',
        retryCount: 0,
        createdAt: Date.now(),
      });
      setPhotos((prev) => [...prev, { mediaId, url }]);
      message.success('照片已暫存（離線可用）');
    } catch (err) {
      // 全域錯誤捕獲：IndexedDB quota / private mode 等異常都會落到這
      console.error('[field-photo] save photo to IndexedDB failed', err);
      message.error('照片暫存失敗');
    } finally {
      // 清空 input value，允許連續拍同一檔名
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  return (
    <Card title="現場拍照" size="small">
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          現場照片（可選，目前僅存於本機，待後續推送至伺服器）
        </Typography.Text>
        {/* 序61：無現場寫入能力（如管理組）不顯示拍照按鈕，只保留說明文字（唯讀） */}
        {canFieldWrite && (
          <>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={(e) => void handlePhotoChange(e)}
            />
            <Button icon={<Camera size={14} />} onClick={() => photoInputRef.current?.click()}>
              拍照
            </Button>
          </>
        )}
        {photos.length > 0 && (
          <Space wrap size="small">
            {photos.map((p) => (
              <img
                key={p.mediaId}
                src={p.url}
                alt="現場照片"
                style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 4 }}
              />
            ))}
          </Space>
        )}
      </Space>
    </Card>
  );
}
